import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Zap } from "lucide-react";

const NICHES = [
  { value: "pressure_washing", label: "Pressure washing" },
  { value: "landscaping", label: "Landscaping" },
  { value: "painting", label: "Painting" },
  { value: "roofing", label: "Roofing" },
  { value: "mobile_detailing", label: "Mobile detailing" },
];

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", business_name: "", niche: "pressure_washing" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created — let's build your first quote.");
      nav("/app/new-estimate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-slate-950 dark relative overflow-hidden grain">
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <Link to="/" className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
              <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-black tracking-tight">QUOTEBOT</span>
          </Link>
          <div className="text-white">
            <p className="font-display text-4xl font-black leading-tight tracking-tight mb-4">
              Stop losing jobs<br /> to slow quotes.
            </p>
            <p className="text-sm text-slate-400">Free while you're getting set up. No card required.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
                <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
              </div>
              <span className="font-display text-2xl font-black tracking-tight">QUOTEBOT</span>
            </Link>
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight mb-2">Start free</h1>
          <p className="text-muted-foreground mb-8">One-minute setup. No card.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm">Business name</Label>
              <Input data-testid="register-business" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="h-12 rounded-sm mt-1" placeholder="Kevin's Wash Co." />
            </div>
            <div>
              <Label className="text-sm">Your niche</Label>
              <Select value={form.niche} onValueChange={(v) => setForm({ ...form, niche: v })}>
                <SelectTrigger data-testid="register-niche" className="h-12 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NICHES.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input data-testid="register-password" type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 rounded-sm mt-1" placeholder="Min 6 characters" />
            </div>
            <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full h-12 rounded-sm btn-kinetic">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Have an account? <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
